import { EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { SearchQuery } from '../models/search-query.model';
import { FboFinderFilter, FBO_AMENITIES_OPTIONS, FBO_FACILITIES_OPTIONS, FBO_FUEL_OPTIONS, FBO_FUEL_TYPE_OPTIONS, FBO_LINE_SERVICES_OPTIONS, FBO_RENTAL_CAR_OPTIONS, FBO_TRANSPORTATION_OPTIONS } from '../../shared/models/fbo-finder-filter.model';
import { FboFinderFiltersService } from '../../shared/services/fbo-finder-filters.service';
import { AnalyticsService } from '../../airport/service/google-analytics-4.service';
/**
 * Filter UI for the Business Finder (FBOs), mirroring {@link FlightSchoolFinderFilterListComponent}. The FBO
 * has many facets, so (like flight schools) its groups are collapsible accordion sections with a
 * selected-count badge, and a selected-filter chip row. Reads and writes the shared
 * {@link FboFinderFiltersService} state and emits `filtersChange`.
 */
var FboFinderFilterListComponent = /** @class */ (function () {
    function FboFinderFilterListComponent(_fboFinderFiltersService, _analyticsService) {
        this._fboFinderFiltersService = _fboFinderFiltersService;
        this._analyticsService = _analyticsService;
        this.filtersChange = new EventEmitter();
        /**
         * The collapsible facet sections, in display order: the group key on FboFinderFilter, the header label, and
         * the checkbox options. Fuel / Fuel Type / Rental Car / Transportation use OR logic; the rest use AND (see
         * filterFbos).
         */
        this.sections = [
            { key: 'amenities', title: 'Amenities', options: FBO_AMENITIES_OPTIONS },
            { key: 'facilities', title: 'Facilities', options: FBO_FACILITIES_OPTIONS },
            { key: 'fuel', title: 'Fuel', options: FBO_FUEL_OPTIONS },
            { key: 'fuelType', title: 'Fuel Type', options: FBO_FUEL_TYPE_OPTIONS },
            { key: 'lineServices', title: 'Line Services', options: FBO_LINE_SERVICES_OPTIONS },
            { key: 'rentalCar', title: 'Rental Car', options: FBO_RENTAL_CAR_OPTIONS },
            { key: 'transportation', title: 'Transportation', options: FBO_TRANSPORTATION_OPTIONS }
        ];
        /**
         * Per-section expand/collapse state. There are far more FBO facets than fit on one screen, so sections
         * start collapsed (compact, no scrolling); any section that already has selected filters is auto-expanded
         * on first load so restored/active filters are visible. Each header shows a count badge.
         */
        this.expanded = {
            amenities: false,
            facilities: false,
            fuel: false,
            fuelType: false,
            lineServices: false,
            rentalCar: false,
            transportation: false
        };
        this._expansionInitialized = false;
        /** Max number of selected-filter chips to render before collapsing the rest into a "+N more" marker. */
        this.maxChips = 5;
        /** The chips actually shown (first {@link maxChips} selected filters, in section + option order). */
        this.visibleChips = [];
        /** How many selected filters are hidden beyond {@link maxChips} (0 = none hidden). */
        this.overflowCount = 0;
        this.fboFinderFilter = new FboFinderFilter();
    }
    Object.defineProperty(FboFinderFilterListComponent.prototype, "nameTooShort", {
        /**
         * True when an FBO name has been entered but is too short to count toward a search. Mirrors the
         * `fboKeyword.length >= 3` gate in {@link SearchQuery.validate}, so the hint shows exactly while the entered
         * name would be ignored.
         */
        get: function () {
            var trimmed = this.fboFinderFilter.name ? this.fboFinderFilter.name.trim() : '';
            return trimmed.length > 0 && trimmed.length < 3;
        },
        enumerable: true,
        configurable: true
    });
    FboFinderFilterListComponent.prototype.ngOnInit = function () {
        var _this = this;
        this._subscription = this._fboFinderFiltersService.fboFinderFilter.subscribe(function (f) {
            _this.fboFinderFilter = f;
            _this._recomputeChips();
            // Auto-expand sections with active filters once (on first load); don't fight the user's manual toggles
            // on subsequent emissions (each checkbox edit re-emits the same filter).
            if (!_this._expansionInitialized) {
                _this._expansionInitialized = true;
                _this.sections.forEach(function (s) { return _this.expanded[s.key] = !!(f && f[s.key] && f[s.key].length); });
            }
        });
        this._analyticsService.pageView('airport_landingpage_fbo', 'FBO Finder Landing Page filters');
    };
    FboFinderFilterListComponent.prototype.ngOnDestroy = function () {
        if (this._subscription) {
            this._subscription.unsubscribe();
        }
    };
    FboFinderFilterListComponent.prototype.isFacetSelected = function (group, value) {
        return this.fboFinderFilter[group].indexOf(value) !== -1;
    };
    FboFinderFilterListComponent.prototype.onToggleFacet = function (group, value, checked) {
        var selected = this.fboFinderFilter[group];
        var i = selected.indexOf(value);
        if (checked && i === -1) {
            selected.push(value);
        }
        else if (!checked && i !== -1) {
            selected.splice(i, 1);
        }
        this._emit();
    };
    FboFinderFilterListComponent.prototype.onSelectAll = function (group, options) {
        this.fboFinderFilter[group] = options.map(function (o) { return o.value; });
        this._emit();
    };
    FboFinderFilterListComponent.prototype.onClearAll = function (group) {
        this.fboFinderFilter[group] = [];
        this._emit();
    };
    FboFinderFilterListComponent.prototype.onNameChange = function () {
        this._emit();
    };
    FboFinderFilterListComponent.prototype.toggleSection = function (group) {
        this.expanded[group] = !this.expanded[group];
    };
    /** Number of selected filters in a section (shown as the header count badge). */
    FboFinderFilterListComponent.prototype.selectedCount = function (group) {
        var selected = this.fboFinderFilter[group];
        return selected ? selected.length : 0;
    };
    FboFinderFilterListComponent.prototype.trackByChip = function (index, chip) {
        return chip.value;
    };
    /**
     * Rebuilds the selected-filter chip list from the current filter state (called on every filter change).
     * Chips are collected in section + option order; only the first {@link maxChips} are shown, the rest are
     * summarized by {@link overflowCount}.
     */
    FboFinderFilterListComponent.prototype._recomputeChips = function () {
        var _this = this;
        var chips = [];
        this.sections.forEach(function (section) {
            var selected = _this.fboFinderFilter[section.key] || [];
            section.options.forEach(function (option) {
                if (selected.indexOf(option.value) !== -1) {
                    chips.push({ group: section.key, value: option.value, label: option.label });
                }
            });
        });
        this.visibleChips = chips.slice(0, this.maxChips);
        this.overflowCount = Math.max(0, chips.length - this.maxChips);
    };
    FboFinderFilterListComponent.prototype._emit = function () {
        // Keep the query's filter in sync as the user edits, so an FBO name entered here counts toward search
        // validation (see SearchQuery.validate) even before the query is serialized/submitted.
        if (this.query) {
            this.query.fboFinderFilter = this.fboFinderFilter;
        }
        this._fboFinderFiltersService.updateFboFinderFilters(this.fboFinderFilter);
        this.filtersChange.emit();
    };
    return FboFinderFilterListComponent;
}());
export { FboFinderFilterListComponent };
